// Übersetzungen für die Self-Service-Seite /termin/:token (Termin ansehen,
// absagen, umbuchen). Wie translations/intake.ts bewusst getrennt von den
// Marketing-Bundles gehalten.

export type ManageLanguage = 'de' | 'en' | 'es' | 'ru';

export interface ManageStrings {
    pageTitle: string;
    loading: string;
    errorLink: string;
    errorGeneric: string;
    labels: {
        treatment: string;
        dateTime: string;
        dentist: string;
        status: string;
    };
    status: {
        pending: string;
        scheduled: string;
        confirmed: string;
        cancelled: string;
        completed: string;
        no_show: string;
    };
    actions: {
        cancel: string;
        reschedule: string;
        back: string;
    };
    tooLate: string;          // {{hours}} Platzhalter
    maxReschedules: string;
    notActionable: string;
    callUs: string;
    cancelFlow: {
        title: string;
        body: string;
        confirm: string;
        keep: string;
        successTitle: string;
        successBody: string;
    };
    rescheduleFlow: {
        title: string;
        pickDay: string;
        pickTime: string;
        noSlots: string;
        confirmTitle: string;  // Bestätigungszeile über dem neuen Slot
        confirm: string;
        submitting: string;
        slotTaken: string;
        successTitle: string;
        successBody: string;
    };
}

const de: ManageStrings = {
    pageTitle: 'Ihr Termin — SeaDent Mallorca',
    loading: 'Termin wird geladen …',
    errorLink: 'Dieser Link ist ungültig oder abgelaufen.',
    errorGeneric: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es später erneut.',
    labels: { treatment: 'Behandlung', dateTime: 'Datum & Uhrzeit', dentist: 'Behandler:in', status: 'Status' },
    status: {
        pending: 'Anfrage eingegangen',
        scheduled: 'Geplant',
        confirmed: 'Bestätigt',
        cancelled: 'Storniert',
        completed: 'Abgeschlossen',
        no_show: 'Nicht erschienen',
    },
    actions: { cancel: 'Termin absagen', reschedule: 'Termin verschieben', back: 'Zurück' },
    tooLate: 'Online-Änderungen sind bis {{hours}} Stunden vor dem Termin möglich.',
    maxReschedules: 'Dieser Termin wurde bereits mehrfach verschoben.',
    notActionable: 'Dieser Termin kann online nicht mehr geändert werden.',
    callUs: 'Bitte rufen Sie uns an: +34 603 31 96 48',
    cancelFlow: {
        title: 'Termin wirklich absagen?',
        body: 'Ihr Termin wird storniert. Sie erhalten eine Bestätigung per E-Mail.',
        confirm: 'Ja, Termin absagen',
        keep: 'Termin behalten',
        successTitle: 'Termin abgesagt',
        successBody: 'Ihr Termin wurde storniert. Wir freuen uns, Sie bald wieder bei uns zu begrüßen.',
    },
    rescheduleFlow: {
        title: 'Neuen Termin wählen',
        pickDay: 'Tag auswählen',
        pickTime: 'Uhrzeit auswählen',
        noSlots: 'An diesem Tag sind leider keine Termine frei.',
        confirmTitle: 'Neuer Termin:',
        confirm: 'Termin verschieben',
        submitting: 'Wird verschoben …',
        slotTaken: 'Dieser Termin wurde gerade vergeben — bitte wählen Sie einen anderen.',
        successTitle: 'Termin verschoben',
        successBody: 'Ihr Termin wurde verschoben. Sie erhalten eine Bestätigung per E-Mail.',
    },
};

const en: ManageStrings = {
    pageTitle: 'Your appointment — SeaDent Mallorca',
    loading: 'Loading your appointment …',
    errorLink: 'This link is invalid or has expired.',
    errorGeneric: 'Something went wrong. Please try again later.',
    labels: { treatment: 'Treatment', dateTime: 'Date & time', dentist: 'Practitioner', status: 'Status' },
    status: {
        pending: 'Request received',
        scheduled: 'Scheduled',
        confirmed: 'Confirmed',
        cancelled: 'Cancelled',
        completed: 'Completed',
        no_show: 'Missed',
    },
    actions: { cancel: 'Cancel appointment', reschedule: 'Reschedule', back: 'Back' },
    tooLate: 'Online changes are possible up to {{hours}} hours before the appointment.',
    maxReschedules: 'This appointment has already been rescheduled several times.',
    notActionable: 'This appointment can no longer be changed online.',
    callUs: 'Please call us: +34 603 31 96 48',
    cancelFlow: {
        title: 'Really cancel this appointment?',
        body: 'Your appointment will be cancelled. You will receive a confirmation by email.',
        confirm: 'Yes, cancel appointment',
        keep: 'Keep appointment',
        successTitle: 'Appointment cancelled',
        successBody: 'Your appointment has been cancelled. We look forward to seeing you again soon.',
    },
    rescheduleFlow: {
        title: 'Pick a new time',
        pickDay: 'Choose a day',
        pickTime: 'Choose a time',
        noSlots: 'Unfortunately there are no free slots on this day.',
        confirmTitle: 'New appointment:',
        confirm: 'Reschedule appointment',
        submitting: 'Rescheduling …',
        slotTaken: 'That slot was just taken — please pick another one.',
        successTitle: 'Appointment rescheduled',
        successBody: 'Your appointment has been moved. You will receive a confirmation by email.',
    },
};

const es: ManageStrings = {
    pageTitle: 'Su cita — SeaDent Mallorca',
    loading: 'Cargando su cita …',
    errorLink: 'Este enlace no es válido o ha caducado.',
    errorGeneric: 'Algo salió mal. Inténtelo de nuevo más tarde.',
    labels: { treatment: 'Tratamiento', dateTime: 'Fecha y hora', dentist: 'Profesional', status: 'Estado' },
    status: {
        pending: 'Solicitud recibida',
        scheduled: 'Programada',
        confirmed: 'Confirmada',
        cancelled: 'Cancelada',
        completed: 'Completada',
        no_show: 'No asistió',
    },
    actions: { cancel: 'Cancelar cita', reschedule: 'Cambiar fecha', back: 'Atrás' },
    tooLate: 'Los cambios en línea son posibles hasta {{hours}} horas antes de la cita.',
    maxReschedules: 'Esta cita ya se ha reprogramado varias veces.',
    notActionable: 'Esta cita ya no se puede modificar en línea.',
    callUs: 'Llámenos: +34 603 31 96 48',
    cancelFlow: {
        title: '¿Cancelar esta cita?',
        body: 'Su cita será cancelada. Recibirá una confirmación por correo electrónico.',
        confirm: 'Sí, cancelar cita',
        keep: 'Mantener cita',
        successTitle: 'Cita cancelada',
        successBody: 'Su cita ha sido cancelada. Esperamos verle pronto de nuevo.',
    },
    rescheduleFlow: {
        title: 'Elegir nueva fecha',
        pickDay: 'Elija un día',
        pickTime: 'Elija una hora',
        noSlots: 'Lamentablemente no hay citas libres este día.',
        confirmTitle: 'Nueva cita:',
        confirm: 'Cambiar la cita',
        submitting: 'Cambiando …',
        slotTaken: 'Esa hora acaba de ocuparse — elija otra, por favor.',
        successTitle: 'Cita reprogramada',
        successBody: 'Su cita ha sido cambiada. Recibirá una confirmación por correo electrónico.',
    },
};

const ru: ManageStrings = {
    pageTitle: 'Ваша запись — SeaDent Mallorca',
    loading: 'Загружаем вашу запись …',
    errorLink: 'Эта ссылка недействительна или устарела.',
    errorGeneric: 'Что-то пошло не так. Пожалуйста, попробуйте позже.',
    labels: { treatment: 'Лечение', dateTime: 'Дата и время', dentist: 'Врач', status: 'Статус' },
    status: {
        pending: 'Запрос получен',
        scheduled: 'Запланирована',
        confirmed: 'Подтверждена',
        cancelled: 'Отменена',
        completed: 'Завершена',
        no_show: 'Неявка',
    },
    actions: { cancel: 'Отменить запись', reschedule: 'Перенести запись', back: 'Назад' },
    tooLate: 'Изменения онлайн возможны не позднее чем за {{hours}} часов до приёма.',
    maxReschedules: 'Эта запись уже переносилась несколько раз.',
    notActionable: 'Эту запись больше нельзя изменить онлайн.',
    callUs: 'Пожалуйста, позвоните нам: +34 603 31 96 48',
    cancelFlow: {
        title: 'Действительно отменить запись?',
        body: 'Ваша запись будет отменена. Вы получите подтверждение по электронной почте.',
        confirm: 'Да, отменить запись',
        keep: 'Оставить запись',
        successTitle: 'Запись отменена',
        successBody: 'Ваша запись отменена. Будем рады видеть вас снова.',
    },
    rescheduleFlow: {
        title: 'Выбрать новое время',
        pickDay: 'Выберите день',
        pickTime: 'Выберите время',
        noSlots: 'К сожалению, в этот день нет свободного времени.',
        confirmTitle: 'Новая запись:',
        confirm: 'Перенести запись',
        submitting: 'Переносим …',
        slotTaken: 'Это время только что заняли — выберите, пожалуйста, другое.',
        successTitle: 'Запись перенесена',
        successBody: 'Ваша запись перенесена. Вы получите подтверждение по электронной почте.',
    },
};

const ALL: Record<ManageLanguage, ManageStrings> = { de, en, es, ru };

export function getManageStrings(language?: string): ManageStrings {
    if (language && language in ALL) return ALL[language as ManageLanguage];
    return ALL.de;
}

// Datums-Locale passend zur Terminsprache.
export function manageDateLocale(language?: string): string {
    switch (language) {
        case 'en': return 'en-GB';
        case 'es': return 'es-ES';
        case 'ru': return 'ru-RU';
        default: return 'de-DE';
    }
}
